import { css, html, LitElement, nothing, type TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { type Action } from "../app-store.ts";
import { type Send, sendNowhere } from "../view.ts";
import * as Styles from "../../styles.ts";
import "../dui/vstack/dui-vstack.ts";
import "../dui/field/dui-field.ts";
import "../dui/field/dui-field-label.ts";
import "../dui/field/dui-field-error.ts";
import "../dui/input/dui-input.ts";
import "../dui/button/dui-button.ts";
import "../dui/icon/dui-icon.ts";
import "../dui/spinner/dui-spinner.ts";

type AuthFieldErrors = {
  emailInvalid: boolean;
  emailMessage?: string;
  /** A general error not attributable to a specific field. */
  generalMessage?: string;
};

/** Validate email before submit. */
const validateEmail = (email: string): AuthFieldErrors => {
  const trimmedEmail = email.trim();

  const emailMessage =
    trimmedEmail.length === 0
      ? "Enter your email"
      : !trimmedEmail.includes("@")
        ? "Enter a valid email"
        : undefined;

  return {
    emailInvalid: emailMessage !== undefined,
    emailMessage,
  };
};

/**
 * Classify a server auth error string into per-field errors.
 */
const classifyAuthError = (error: string | undefined): AuthFieldErrors => {
  if (!error) {
    return { emailInvalid: false };
  }

  const lower = error.toLowerCase();

  // Email-specific errors
  if (lower.includes("email")) {
    const cleaned = error
      .replace(/,?\s*or\s*(a\s*)?phone\s*number/gi, "")
      .replace(/either\s+/gi, "")
      .replace(/\s{2,}/g, " ")
      .trim();
    return {
      emailInvalid: true,
      emailMessage: cleaned,
    };
  }

  // General errors
  return {
    emailInvalid: false,
    generalMessage: error,
  };
};

const styles = css`
  :host {
    display: block;
    width: 100%;
    max-width: 25rem;
  }

  .auth {
    display: block;
    padding: var(--space-6);
    border-radius: var(--radius-md);
    border: 1px solid var(--border);
    background-color: var(--card);
    color: var(--card-foreground);
  }

  .auth-title {
    font-size: var(--font-size-xl);
    line-height: var(--line-height-snug);
    text-align: center;
  }

  form {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
  }

  .general-error-wrapper {
    min-height: var(--space-px);
  }

  .general-error {
    font-family: var(--font-sans);
    font-size: var(--font-size-sm);
    line-height: var(--line-height-normal);
    text-align: center;
    color: var(--destructive);
    background-color: color-mix(in oklch, var(--destructive) 8%, transparent);
    height: var(--component-height-lg);
    border-radius: var(--radius-md);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  dui-button {
    display: block;
    --button-width: 100%;
  }

  .otp-sent {
    font-family: var(--font-sans);
    font-size: var(--font-size-sm);
    line-height: var(--line-height-normal);
    text-align: center;
    color: var(--muted-foreground);
  }
`;

/**
 * Authentication view component for magic link sign-in.
 */
@customElement("auth-view")
export class AuthView extends LitElement {
  static override styles = [Styles.base, styles];

  @property({ type: String })
  accessor error: string | undefined = undefined;

  @property({ type: Boolean })
  accessor isLoading = false;

  @property({ type: Boolean })
  accessor otpSent = false;

  @property({ attribute: false })
  accessor send: Send<Action> = sendNowhere;

  @state()
  accessor #email = "";

  @state()
  accessor #validationErrors: AuthFieldErrors = {
    emailInvalid: false,
  };

  /** When true, the server error is suppressed (user has started editing). */
  @state()
  accessor #serverErrorDismissed = false;

  #clearErrors = (): void => {
    this.#validationErrors = { emailInvalid: false };
    this.#serverErrorDismissed = true;
  };

  #submit = (): void => {
    if (this.isLoading) return;

    const validation = validateEmail(this.#email);
    if (validation.emailInvalid) {
      this.#validationErrors = validation;
      return;
    }

    this.send({ type: "clear-auth-error" });
    this.#serverErrorDismissed = false;

    this.send({
      type: "sign-in",
      email: this.#email,
    });
  };

  #handleSubmit = (e: Event): void => {
    e.preventDefault();
    this.#submit();
  };

  #handleKeydown = (e: KeyboardEvent): void => {
    if (e.key === "Enter") {
      e.preventDefault();
      this.#submit();
    }
  };

  #handleEmailChange = (e: CustomEvent<{ value: string }>): void => {
    this.#email = e.detail.value;
    this.#clearErrors();
  };

  override render(): TemplateResult {
    if (this.otpSent) {
      return html`
        <dui-vstack class="auth">
          <h2 class="auth-title">Check your email</h2>
          <p class="otp-sent">
            We sent a sign-in link to <strong>${this.#email}</strong>. Click the
            link in the email to sign in.
          </p>
        </dui-vstack>
      `;
    }

    const hasValidationErrors = this.#validationErrors.emailInvalid;
    const serverError = this.#serverErrorDismissed ? undefined : this.error;
    const errors = hasValidationErrors
      ? this.#validationErrors
      : classifyAuthError(serverError);

    return html`
      <dui-vstack class="auth">
        <h2 class="auth-title">Sign In</h2>

        <form @keydown="${this.#handleKeydown}">
          <dui-field ?invalid="${errors.emailInvalid}">
            <dui-field-label>Email</dui-field-label>
            <dui-input
              type="email"
              name="email"
              placeholder="name@example.com"
              autocomplete="email"
              required
              autofocus
              ?disabled="${this.isLoading}"
              @input-change="${this.#handleEmailChange}"
            ></dui-input>
            ${errors.emailMessage
              ? html`<dui-field-error>${errors.emailMessage}</dui-field-error>`
              : nothing}
          </dui-field>

          <div class="general-error-wrapper">
            ${errors.generalMessage
              ? html`<p class="general-error" role="alert">
                  ${errors.generalMessage}
                </p>`
              : nothing}
          </div>

          <dui-button
            type="submit"
            ?disabled="${this.isLoading}"
            @click="${this.#handleSubmit}"
          >
            ${this.isLoading
              ? html`<dui-spinner size="sm"></dui-spinner>`
              : "Sign in with email"}
          </dui-button>
        </form>
      </dui-vstack>
    `;
  }
}
